// src/pages/PortalSaaS/modules/MeusLaudos.js
import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { db, storage } from '../../../firebase';
import { collection, query, getDocs, where, orderBy } from 'firebase/firestore';
import { ref, getDownloadURL } from "firebase/storage";
import TabelaCRM from '../components/TabelaCRM';

const MeusLaudos = () => {
    const { workspaceData } = useOutletContext();
    const [laudos, setLaudos] = useState([]);
    const [isProcessing, setIsProcessing] = useState(true);

    const isExtranetView = workspaceData?.role === 'EXTRANET';

    // =========================================================================
    // 1. CARREGAMENTO DOS DADOS (ISOLAMENTO MULTITENANT E RBAC)
    // =========================================================================
    const carregarLaudos = async () => {
        if (!workspaceData?.cnpj_completo) return;
        setIsProcessing(true);
        
        try {
            // Buscamos apenas os tickets que sobreviveram à IA (Aguardando Pagamento ou Emitidos)
            let qTickets;

            if (isExtranetView) {
                // EXTRANET: Vê apenas os laudos autorizados para o CNPJ dele
                qTickets = query(
                    collection(db, 'workspaces', workspaceData.cnpj_completo, 'crm_tickets'),
                    where('cliente_cnpj_visibilidade', '==', workspaceData.documento_fiscal),
                    where('status', 'in', ['AGUARDANDO_PAGAMENTO', 'LAUDO_EMITIDO'])
                );
            } else {
                // LABORATÓRIO (Gestão): Vê o faturamento/entrega de todos os clientes
                qTickets = query(
                    collection(db, 'workspaces', workspaceData.cnpj_completo, 'crm_tickets'),
                    where('status', 'in', ['AGUARDANDO_PAGAMENTO', 'LAUDO_EMITIDO'])
                );
            }

            const snap = await getDocs(qTickets);
            
            // Ordenação manual no front-end para evitar necessidade de criação de Índices Compostos no Firebase (UX fluida)
            const lista = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            lista.sort((a, b) => b.created_at?.toMillis() - a.created_at?.toMillis());
            
            setLaudos(lista);
        } catch (error) {
            console.error("Erro ao buscar laudos: ", error);
            alert("Não foi possível carregar a lista de laudos no momento.");
        } finally {
            setIsProcessing(false);
        }
    };

    useEffect(() => {
        carregarLaudos();
    }, [workspaceData, isExtranetView]);

    // =========================================================================
    // 2. AÇÕES DE INTERAÇÃO (PAYWALL E DOWNLOAD)
    // =========================================================================
    const handlePagar = (ticket) => {
        // Na Fase 3, o back-end gravará a URL de checkout do Asaas no ticket.
        if (ticket.asaas_payment_url) {
            window.open(ticket.asaas_payment_url, '_blank');
        } else {
            alert("⏳ O link de pagamento seguro está sendo processado pelo gateway. Tente novamente em instantes.");
        }
    };

    const handleDownload = async (ticket) => {
        if (!ticket.arquivo_path) {
            return alert("O arquivo físico não foi encontrado na base de dados.");
        }
        
        try {
            // Busca a URL assinada e temporária no Storage (mantendo o arquivo privado)
            const fileRef = ref(storage, ticket.arquivo_path);
            const url = await getDownloadURL(fileRef);
            window.open(url, '_blank');
        } catch (error) {
            console.error("Erro ao baixar PDF:", error);
            alert("Acesso negado ou arquivo inexistente. O laboratório pode ter removido o laudo.");
        }
    };

    // =========================================================================
    // 3. DEFINIÇÃO DA TABELA (AGNÓSTICA)
    // =========================================================================
    const colunas = useMemo(() => [
        { 
            header: 'Data de Emissão', 
            render: (row) => row.created_at ? new Date(row.created_at.toDate()).toLocaleDateString('pt-BR') : '--'
        },
        { 
            header: 'Ensaio / Norma', 
            accessor: 'tipo_ensaio' 
        },
        { 
            header: 'Amostra (QR)', 
            accessor: 'amostra_id' 
        },
        { 
            header: 'Geolocalização', 
            render: (row) => row.dados_brutos?.km ? `Estaca/KM: ${row.dados_brutos.km}` : 'Lab/Bancada'
        },
        { 
            header: 'Status de Entrega', 
            render: (row) => {
                if (row.status === 'AGUARDANDO_PAGAMENTO') {
                    return <span style={{ color: '#d97706', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px' }}>🔒 Retido (Pendente)</span>;
                }
                return <span style={{ color: '#16a34a', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '5px' }}>✅ Liberado</span>;
            }
        },
        { 
            header: 'Ação Segura', 
            render: (row) => {
                if (row.status === 'AGUARDANDO_PAGAMENTO') {
                    return (
                        <button 
                            onClick={(e) => { e.stopPropagation(); handlePagar(row); }}
                            style={{ 
                                background: '#f59e0b', color: '#fff', border: 'none', padding: '8px 15px', 
                                borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', width: '100%' 
                            }}
                        >
                            💳 Pagar para Liberar
                        </button>
                    );
                }
                return (
                    <button 
                        onClick={(e) => { e.stopPropagation(); handleDownload(row); }}
                        style={{ 
                            background: '#0f172a', color: '#fff', border: 'none', padding: '8px 15px', 
                            borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', width: '100%' 
                        }}
                    >
                        📥 Baixar PDF Timbrado
                    </button>
                );
            }
        }
    ], []);

    // =========================================================================
    // 4. RENDERIZAÇÃO DA INTERFACE
    // =========================================================================
    return (
        <div className="pts-fade-in" style={{ padding: '30px', height: '100%', overflowY: 'auto', background: '#f8fafc' }}>
            <header className="pts-view-header" style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                    <h1 style={{ color: '#0f172a', margin: '0 0 5px 0', fontSize: '1.8rem', fontWeight: '800' }}>
                        {isExtranetView ? 'Meus Laudos' : 'Faturamento e Entregas'}
                    </h1>
                    <p style={{ color: '#64748b', margin: 0, fontSize: '0.95rem' }}>
                        {isExtranetView 
                            ? `Repositório oficial de ensaios periciados pela IA para a sua empresa.`
                            : `Acompanhe a liquidação de pagamentos e a liberação de laudos dos seus clientes.`}
                    </p>
                </div>
                <div className="pts-header-actions">
                    <button className="pts-btn-outline" onClick={carregarLaudos} disabled={isProcessing} style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer', fontWeight: '600' }}>
                        {isProcessing ? 'Sincronizando...' : '🔄 Atualizar Status'}
                    </button>
                </div>
            </header>

            <div style={{ boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', borderRadius: '12px' }}>
                <TabelaCRM 
                    columns={colunas} 
                    data={laudos} 
                    emptyMessage={isProcessing ? "Buscando dados seguros..." : "Nenhum laudo encontrado no seu cofre digital."}
                />
            </div>
            
            {isExtranetView && (
                <p style={{ fontSize: '0.75rem', color: '#94a3b8', textAlign: 'center', marginTop: '20px' }}>
                    🔒 O faturamento é processado pela infraestrutura financeira <strong>Asaas</strong> através da Primo Trade Solutions.
                </p>
            )}
        </div>
    );
};

export default MeusLaudos;